/* AlertCenter
   Primary operational alert workflow surface — mounted in TriageCenter.jsx
   per AbdulRafiu's Q3 decision (Patient Safety Actions authorisation,
   02/09/2026): "The primary operational AlertCenter.jsx should be mounted
   in the Triage Centre."

   Wired against the 5.6.1 alert routes as built:
     GET  /alerts            (status, severity, patientId query params)
     GET  /alerts/summary
     POST /alerts/:id/acknowledge
     POST /alerts/:id/resolve

   No escalate action is exposed here. 5.6.4 (alert-native escalation) is
   on hold pending AbdulRafiu's review of the design proposal — the
   backend has no escalate transition yet (VALID_TRANSITIONS only defines
   acknowledge/resolve), so this UI must not imply one exists.

*/
import React, { useState, useEffect, useCallback } from "react";
import * as FiIcons from "react-icons/fi";
const { FiAlertTriangle, FiFilter, FiRefreshCw } = FiIcons;
import SafeIcon from "../common/SafeIcon";
import api from "../lib/api";
import toast from "react-hot-toast";

const SEVERITY_STYLES = {
  critical: "bg-rose-500/10 border-rose-500/30 text-rose-400",
  high: "bg-orange-500/10 border-orange-500/30 text-orange-400",
  medium: "bg-amber-500/10 border-amber-500/30 text-amber-400",
  low: "bg-slate-500/10 border-slate-500/30 text-slate-400",
};

const STATUS_LABEL = {
  open: "Open",
  acknowledged: "Acknowledged",
  escalated: "Escalated",
  closed: "Closed",
};

const STATUS_FILTERS = ["open", "acknowledged", "escalated", "closed", ""];

export default function AlertCenter() {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");
  const [severityFilter, setSeverityFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = severityFilter;
      const [alertsRes, summaryRes] = await Promise.all([
        api.get("/alerts", { params }),
        api.get("/alerts/summary"),
      ]);
      setAlerts(alertsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      toast.error("Failed to load alerts");
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, severityFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAction = async (id, action) => {
    setActingId(id);
    try {
      await api.post(`/alerts/${id}/${action}`);
      toast.success(action === "acknowledge" ? "Alert acknowledged" : "Alert resolved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to ${action} alert`);
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-4 lg:p-6 backdrop-blur-xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 lg:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-rose-600/20 rounded-2xl flex items-center justify-center">
            <SafeIcon icon={FiAlertTriangle} className="text-rose-400 text-xl" />
          </div>
          <div>
            <h2 className="text-base lg:text-xl font-bold">Alert Center</h2>
            <p className="text-xs text-slate-400">Tenant-wide clinical alerts</p>
          </div>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-white border border-slate-700 px-3 py-1.5 rounded-lg self-start sm:self-auto"
        >
          <SafeIcon icon={FiRefreshCw} className={isLoading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {["critical", "high", "medium", "low"].map((sev) => (
            <div key={sev} className={`rounded-2xl border p-3 ${SEVERITY_STYLES[sev]}`}>
              <div className="text-[10px] font-black uppercase tracking-widest">{sev}</div>
              <div className="text-2xl font-black">{summary[sev] ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <SafeIcon icon={FiFilter} className="text-slate-500" />
        {STATUS_FILTERS.map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest ${
              statusFilter === s
                ? "bg-primary-600 text-white"
                : "bg-slate-800 text-slate-400 hover:text-white"
            }`}
          >
            {s ? STATUS_LABEL[s] : "All"}
          </button>
        ))}
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="ml-auto bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs"
        >
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
        {isLoading && alerts.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-8">Loading alerts…</p>
        )}
        {!isLoading && alerts.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-8">No alerts match this filter.</p>
        )}
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-950/60 border border-slate-800 rounded-2xl p-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span
                className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-lg border flex-shrink-0 ${
                  SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.low
                }`}
              >
                {alert.severity}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold truncate">
                  {alert.title || alert.message || `Alert ${alert.id}`}
                </p>
                <p className="text-xs text-slate-500">
                  Patient {alert.patient_id} • {STATUS_LABEL[alert.status] || alert.status}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {alert.status === "open" && (
                <button
                  disabled={actingId === alert.id}
                  onClick={() => handleAction(alert.id, "acknowledge")}
                  className="text-xs font-bold text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-lg hover:bg-amber-500/10 disabled:opacity-40"
                >
                  Acknowledge
                </button>
              )}
              {["open", "acknowledged", "escalated"].includes(alert.status) && (
                <button
                  disabled={actingId === alert.id}
                  onClick={() => handleAction(alert.id, "resolve")}
                  className="text-xs font-bold text-emerald-400 border border-emerald-500/30 px-3 py-1.5 rounded-lg hover:bg-emerald-500/10 disabled:opacity-40"
                >
                  Resolve
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-slate-600 mt-4">
        Escalation actions aren't available here yet — 5.6.4 is on hold pending architecture sign-off.
      </p>
    </div>
  );
}